<template>
  <div>
    <!-- Overlay de Loading para carregamento inicial -->
    <div v-if="isLoading && !isSubmitting" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="mt-3 mb-0">Carregando dados do alerta...</p>
      </div>
    </div>

    <!-- Overlay de Loading para submissão -->
    <div v-if="isSubmitting" class="loading-overlay submitting-overlay">
      <div class="loading-content">
        <div class="spinner-border text-success" role="status" style="width: 4rem; height: 4rem;">
          <span class="visually-hidden">Processando...</span>
        </div>
        <h5 class="mt-4 mb-2 text-success">{{ submitMessage }}</h5>
        <p class="text-muted mb-0">Aguarde enquanto processamos sua solicitação...</p>
        <div class="progress mt-3" style="width: 250px;">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-success" 
               role="progressbar" 
               style="width: 100%">
          </div>
        </div>
      </div>
    </div>

    <!-- Form -->
    <div class="container-fluid" :class="{ 'form-loading': isLoading || isSubmitting }">
      <div class="row mb-3">
        <div class="col-12">
          <label class="form-label">Nome do Alerta <span class="text-danger">*</span></label>
          <input 
            type="text" 
            class="form-control" 
            v-model="formData.nome"
            placeholder="Digite o nome do alerta"
            :disabled="isLoading || isSubmitting"
          />
        </div>
      </div>
      
      <!-- Origem do dispositivo: decide todo o resto do formulário. Dispositivos do OnAgro
           são organizados por propriedade; os de terceiros, por órgão (Defesa Civil, ANA...).
           Sem estações de terceiros liberadas, o seletor some e o form fica no fluxo OnAgro. -->
      <div class="row mb-3" v-if="temDispositivosExternos">
        <div class="col-12">
          <label class="form-label origem-label">Origem do dispositivo <span class="text-danger">*</span></label>
          <div class="origem-toggle">
            <button
              type="button"
              :class="['origem-option', { active: !isExternalMode }]"
              @click="setOrigem('onagro')"
              :disabled="isLoading || isSubmitting"
            >
              OnAgro
            </button>
            <button
              type="button"
              :class="['origem-option', { active: isExternalMode }]"
              @click="setOrigem('terceiros')"
              :disabled="isLoading || isSubmitting"
            >
              Terceiros
            </button>
          </div>
        </div>
      </div>

      <div class="row mb-3">
        <!-- OnAgro: propriedade → dispositivo -->
        <div class="col-md-6" v-if="!isExternalMode">
          <label class="form-label">Propriedade <span class="text-danger">*</span></label>
          <VueMultiselect
            v-model="selectedPropriedadeObj"
            :options="propriedadeOptions"
            :searchable="true"
            :show-labels="false"
            placeholder="Selecione uma propriedade"
            label="nome"
            track-by="id"
            :max-height="300"
            @select="onPropriedadeSelect"
            :disabled="isLoading || isSubmitting"
          >
            <template v-slot:noOptions>
              Nenhuma propriedade encontrada
            </template>
          </VueMultiselect>
        </div>

        <div class="col-md-6" v-if="!isExternalMode">
          <label class="form-label">Dispositivo <span class="text-danger">*</span></label>
          <VueMultiselect
            v-model="selectedDispositivoObj"
            :options="dispositivos"
            :searchable="true"
            :show-labels="false"
            :placeholder="loadingDispositivos ? 'Carregando dispositivos...' : 'Selecione um dispositivo'"
            label="nome"
            track-by="id"
            :max-height="300"
            @select="onDispositivoSelect"
            :disabled="!formData.propriedadeId || isLoading || loadingDispositivos || isSubmitting"
          >
            <template v-slot:noOptions>
              Nenhum dispositivo encontrado
            </template>
          </VueMultiselect>
        </div>

        <!-- Terceiros: órgão → estação -->
        <div class="col-md-6" v-if="isExternalMode">
          <label class="form-label">Órgão <span class="text-danger">*</span></label>
          <VueMultiselect
            v-model="selectedFonteObj"
            :options="fonteOptions"
            :searchable="false"
            :show-labels="false"
            :placeholder="loadingFontes ? 'Carregando órgãos...' : 'Selecione o órgão'"
            label="label"
            track-by="fonte"
            :max-height="300"
            @select="onFonteSelect"
            :disabled="isLoading || loadingFontes || isSubmitting"
          >
            <template v-slot:noOptions>
              Nenhum órgão disponível
            </template>
          </VueMultiselect>
        </div>

        <div class="col-md-6" v-if="isExternalMode">
          <label class="form-label">Dispositivo <span class="text-danger">*</span></label>
          <VueMultiselect
            v-model="selectedExternalDeviceObj"
            :options="externalDeviceOptions"
            :searchable="true"
            :show-labels="false"
            :placeholder="externalDevicePlaceholder"
            label="nomeExibicao"
            track-by="id"
            :max-height="300"
            @select="onExternalDeviceSelect"
            :disabled="!formData.fonte || isLoading || loadingExternalDevices || isSubmitting"
          >
            <template v-slot:noOptions>
              Nenhum dispositivo encontrado para este órgão
            </template>
          </VueMultiselect>
          <small class="text-muted" v-if="selectedExternalDeviceObj && selectedExternalDeviceObj.rio">
            Rio {{ selectedExternalDeviceObj.rio }}
          </small>
        </div>
      </div>

      <!-- Sensor: define quais Dados e Unidades ficam disponíveis nas condições.
           Não se aplica a dispositivos de terceiros: lá as grandezas vêm da própria estação. -->
      <div class="row mb-3" v-if="!isExternalMode">
        <div class="col-12">
          <label class="form-label">Sensor <span class="text-danger">*</span></label>
          <VueMultiselect
            v-model="selectedSensorObj"
            :options="sensorOptions"
            :searchable="true"
            :show-labels="false"
            :placeholder="loadingSensors ? 'Carregando sensores...' : 'Selecione um sensor'"
            label="name"
            track-by="linkname"
            :max-height="300"
            @select="onSensorSelect"
            :disabled="!formData.dispositivoId || isLoading || loadingSensors || isSubmitting"
          >
            <template v-slot:noOptions>
              Nenhum sensor encontrado
            </template>
          </VueMultiselect>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12">
          <label class="form-label">Frequência de Alerta <span class="text-danger">*</span></label>
          <VueMultiselect
            v-model="selectedAlertFrequencyObj"
            :options="alertFrequencyOptionsArray"
            :searchable="false"
            :show-labels="false"
            placeholder="Selecione a frequência"
            label="label"
            track-by="value"
            :max-height="300"
            @select="onAlertFrequencySelect"
            :disabled="isLoading || isSubmitting"
          >
          </VueMultiselect>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12">
          <label class="form-label">Tipo de alerta <span class="text-danger">*</span></label>
          <VueMultiselect
            v-model="selectedAlertTypeObj"
            :options="alertTypeOptionsArray"
            :searchable="false"
            :show-labels="false"
            placeholder="Selecione o tipo de alerta"
            label="label"
            track-by="value"
            :max-height="300"
            @select="onAlertTypeSelect"
            :disabled="isLoading || isSubmitting"
          >
          </VueMultiselect>
        </div>
      </div>

      <div class="dados-container mb-3">
        <h5 class="mb-3">Condições do Alerta <span class="text-danger">*</span></h5>
        <div v-for="(condicao, index) in formData.condicoes" :key="'condicao-' + index" class="dados-item mb-4">
          <div class="row mb-2">
            <!-- Grid progressivo: 4 colunas só em telas grandes. Em tablet (col-md, ~768px)
                 quatro selects lado a lado ficavam com ~90px e cortavam o texto. -->
            <div class="col-12 col-sm-6 col-lg-3 mb-2 mb-lg-0">
              <label class="form-label">Dado <span class="text-danger">*</span></label>
              <VueMultiselect
                v-model="condicao.selectedDataToAlertObj"
                :options="dataToAlertOptionsArray"
                :searchable="false"
                :show-labels="false"
                :placeholder="dadoPlaceholder"
                label="label"
                track-by="value"
                :max-height="300"
                @input="(selected) => onDataToAlertSelect(condicao, selected, index)"
                :disabled="!fieldsDisponiveis || isLoading || isSubmitting"
              >
                <template v-slot:noOptions>
                  {{ dadoPlaceholder }}
                </template>
              </VueMultiselect>
            </div>
            <div class="col-12 col-sm-6 col-lg-3 mb-2 mb-lg-0">
              <label class="form-label">Operação <span class="text-danger">*</span></label>
              <VueMultiselect
                v-model="condicao.selectedOperatorObj"
                :options="operationTypeOptionsArray"
                :searchable="false"
                :show-labels="false"
                placeholder="Selecione uma operação"
                label="label"
                track-by="value"
                :max-height="300"
                @input="(selected) => onOperatorSelect(condicao, selected, index)"
                :disabled="isLoading || isSubmitting"
              >
              </VueMultiselect>
            </div>
            <div class="col-12 col-sm-6 col-lg-3 mb-2 mb-lg-0">
              <label class="form-label">Valor <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                  type="number"
                  class="form-control"
                  v-model="condicao.value"
                  placeholder="Digite o valor"
                  step="0.01"
                  :disabled="isLoading || isSubmitting"
                />
              </div>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <label class="form-label">Unidade <span class="text-danger">*</span></label>
              <VueMultiselect
                v-model="condicao.selectedUnitObj"
                :options="getAvailableUnitsArray(condicao)"
                :searchable="false"
                :show-labels="false"
                :placeholder="!condicao.dataToAlert && condicao.dataToAlert !== 0 ? 'Selecione um dado primeiro' : 'Selecione uma unidade'"
                label="label"
                track-by="value"
                :max-height="300"
                @input="(selected) => onUnitSelect(condicao, selected, index)"
                :disabled="!condicao.dataToAlert && condicao.dataToAlert !== 0 || isLoading || isSubmitting"
              >
                <template v-slot:noOptions>
                  {{ !condicao.dataToAlert && condicao.dataToAlert !== 0 ? 'Selecione um dado primeiro' : 'Nenhuma unidade encontrada' }}
                </template>
              </VueMultiselect>
            </div>
          </div>

          <button 
            v-if="index > 0" 
            type="button" 
            class="btn btn-danger btn-sm mt-2"
            @click="removerCondicao(index)"
            :disabled="isLoading || isSubmitting"
          >
            <i class="fas fa-trash"></i> Remover
          </button>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-12">
          <button 
            type="button" 
            class="btn btn-success"
            @click="adicionarCondicao"
            :disabled="isLoading || isSubmitting"
          >
            <i class="fas fa-plus"></i> Adicionar condição
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <p class="text-muted small">
            <span class="text-danger">*</span> Campos obrigatórios
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueMultiselect from 'vue-multiselect';

export default {
  name: 'AlertaForm',
  components: {
    VueMultiselect
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    propriedades: {
      type: Array,
      default: () => []
    },
    dispositivos: {
      type: Array,
      default: () => []
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      loadingDispositivos: false,
      loadingSensors: false,
      // --- Dispositivos de terceiros (Defesa Civil / ANA / TideSat) ---
      loadingFontes: false,
      loadingExternalDevices: false,
      // Só exibe o seletor de origem se houver estação de terceiros liberada para o usuário.
      temDispositivosExternos: false,
      selectedFonteObj: null,
      selectedExternalDeviceObj: null,
      fonteOptions: [],
      externalDeviceOptions: [],
      // Sensor selecionado e catálogo de sensores do dispositivo
      selectedSensorObj: null,
      sensorOptions: [],
      // Campos (fields) do sensor selecionado, vindos do backend.
      // Definem quais Dados e Unidades ficam disponíveis nas condições.
      selectedSensorFields: [],
      // Objetos para VueMultiselect
      selectedPropriedadeObj: null,
      selectedDispositivoObj: null,
      selectedAlertFrequencyObj: null,
      selectedAlertTypeObj: null,
      alertFrequencyOptions: {
        // 0: 'Nenhum',
        1: 'Cada 15 Minutos',
        2: 'Cada 30 Minutos',
        3: 'Cada 1 Hora',
        4: 'Cada 6 Horas',
        5: 'Cada 12 Horas',
        6: 'Cada 24 Horas',
        7: 'Cada 7 Dias',
        8: 'Cada 30 Dias'
      },
      alertTypeOptions: {
        0: 'Unitário (dispara com uma condição)',
        1: 'Total (dispara apenas quando todas as condições forem atingidas)'
      },
      dataToAlertOptions: {
        0: 'Temperatura',
        1: 'Velocidade',
        2: 'Umidade',
        3: 'Luminosidade',
        4: 'Pressão',
        5: 'Profundidade',
        // 6: 'Volume',
        7: 'Precipitação'
      },
      operationTypeOptions: {
        0: 'Igual a',
        1: 'Maior que',
        2: 'Menor que',
        3: 'Maior ou igual a',
        4: 'Menor ou igual a'
      },
      // Mapeamento de unidades por tipo de dado
      unitsByDataType: {
        0: { // Temperatura
          0: '°C',    // Celsius = 0
          1: '°F',    // Fahrenheit = 1 
          2: 'K'      // Kelvin = 2
        },
        1: { // Velocidade
          3: 'km/h',  // KilometersPerHour = 3
          4: 'm/s'    // MetersPerSecond = 4
        },
        2: { // Umidade
          5: '%'      // Percentage = 5 ✅ CORRIGIDO
        },
        3: { // Luminosidade
          6: 'lux',   // Lux = 6
          7: 'm²'     // SquareMeters = 7
        },
        4: { // Pressão
          8: 'atm',   // Atmospheres = 8
          9: 'hPa',   // Hectopascals = 9
          10: 'mmHg'  // MillimetersOfMercury = 10
        },
        5: { // Profundidade
          11: 'm',    // Meters = 11
          12: 'cm',   // Centimeters = 12
          13: 'mm'    // Millimeters = 13
        },
        6: { // Volume
          14: 'cm³',  // CubicCentimeters = 14
          15: 'mm³'   // CubicMillimeters = 15
        },
        7: { // Precipitação
          18: 'mm'    // MillimetersRain = 18
        }
      },
      // Grandezas alertáveis de um dispositivo de terceiros, por sensor declarado pela fonte
      // (bloco "sensores" do cadastro: {"nivel":true,"chuva":true,...}).
      //
      // NOTA: nível de rio e vazão ainda não existem no catálogo interno (DataToAlert). Até
      // que sejam adicionados, o alerta de nível reusa Profundidade (5) — mesma dimensão
      // física (comprimento) e mesmas unidades m/cm/mm, então a conversão já funciona.
      // Chuva usa Precipitação (7) e o bloco "estacao" reusa temperatura/umidade/pressão/vento,
      // que são idênticos aos do OnAgro.
      externalFieldsBySensor: {
        nivel: [
          // Metros como padrão: é a unidade em que o nível é exibido no mapa e no aside,
          // então o valor do alerta fica na mesma escala que o usuário está vendo.
          { dataToAlert: 5, label: 'Nível do rio', defaultUnit: 11, availableUnits: [11, 12, 13] },
        ],
        chuva: [
          { dataToAlert: 7, label: 'Chuva', defaultUnit: 18, availableUnits: [18] },
        ],
        estacao: [
          { dataToAlert: 0, label: 'Temperatura', defaultUnit: 0, availableUnits: [0, 1, 2] },
          { dataToAlert: 2, label: 'Umidade', defaultUnit: 5, availableUnits: [5] },
          { dataToAlert: 4, label: 'Pressão', defaultUnit: 9, availableUnits: [9, 8, 10] },
          { dataToAlert: 1, label: 'Velocidade do vento', defaultUnit: 4, availableUnits: [4, 3] },
        ],
        // "vazao" fica de fora: não há grandeza equivalente no catálogo interno e mapeá-la
        // para outra seria mentir sobre a unidade. Entra quando Discharge existir no enum.
      },
      // Valores padrão para cada tipo de dado
      defaultUnits: {
        0: 0,  // Temperatura: Celsius = 0
        1: 3,  // Velocidade: KilometersPerHour = 3
        2: 5,  // Umidade: Percentage = 5
        3: 6,  // Luminosidade: Lux = 6
        4: 8,  // Pressão: Atmospheres = 8
        5: 11, // Profundidade: Meters = 11
        6: 15, // Volume: CubicMillimeters = 15
        7: 18  // Precipitação: MillimetersRain = 18
      }
    };
  },
  computed: {
    formData() {
      return this.form.crud.form;
    },
    submitMessage() {
      const isEditing = this.formData && this.formData.id;
      return isEditing ? 'Salvando alerta...' : 'Criando alerta...';
    },
    propriedadeOptions() {
      const options = [...this.propriedades];
      if (!this.isAdmin()) {
        options.push({ id: 'shared', nome: 'Compartilhados Comigo' });
      }
      return options;
    },

    /// Modo "dispositivo de terceiros": troca propriedade/dispositivo/sensor por órgão+estação.
    /// Controlado pelo seletor de Origem no topo do form (não por uma opção do select de
    /// propriedade — origem e propriedade são decisões distintas).
    isExternalMode() {
      return this.formData.origem === 'terceiros';
    },

    externalDevicePlaceholder() {
      if (this.loadingExternalDevices) return 'Carregando dispositivos...';
      if (!this.formData.fonte) return 'Selecione a fonte primeiro';
      return 'Selecione um dispositivo';
    },

    /// Há grandezas para escolher? (sensor no fluxo próprio, estação no de terceiros)
    fieldsDisponiveis() {
      return this.selectedSensorFields && this.selectedSensorFields.length > 0;
    },

    dadoPlaceholder() {
      if (this.fieldsDisponiveis) return 'Selecione um dado';
      return this.isExternalMode
        ? 'Selecione um dispositivo primeiro'
        : 'Selecione um sensor primeiro';
    },
    dispositivoOptions() {
      return this.dispositivos;
    },
    alertFrequencyOptionsArray() {
      return Object.entries(this.alertFrequencyOptions).map(([value, label]) => ({
        value: Number(value),
        label: label
      }));
    },
    alertTypeOptionsArray() {
      return Object.entries(this.alertTypeOptions).map(([value, label]) => ({
        value: Number(value),
        label: label
      }));
    },
    dataToAlertOptionsArray() {
      // Os Dados disponíveis vêm do sensor selecionado (backend), não mais da
      // lista fixa. Sem sensor selecionado, não há dados para escolher.
      if (!this.selectedSensorFields || !this.selectedSensorFields.length) {
        return [];
      }
      return this.selectedSensorFields.map(f => ({
        value: Number(f.dataToAlert),
        // Usa o label do backend; se ausente, cai no rótulo local por índice.
        label: f.label || this.dataToAlertOptions[Number(f.dataToAlert)] || String(f.dataToAlert)
      }));
    },
    operationTypeOptionsArray() {
      return Object.entries(this.operationTypeOptions).map(([value, label]) => ({
        value: Number(value),
        label: label
      }));
    }
  },
  async created() {
    if (!this.formData) {
      console.error("Formulário não recebeu dados válidos");
      return;
    }

    this.carregarDisponibilidadeExterna();

    if (!this.isSubmitting) {
      this.isLoading = true;
    }
    
    try {
      this.inicializarFormulario();
      this.mapearCampos();
      
      if (this.formData.propriedadeId && this.formData.id) {
        await this.$nextTick();
        await this.loadDispositivosByPropriedade(this.formData.propriedadeId);
      }
    } catch (error) {
      console.error("Erro ao inicializar formulário:", error);
      this.showAlert("Erro", "Erro ao carregar dados do formulário", "bg-danger");
    } finally {
      if (!this.isSubmitting) {
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      }
    }
  },
  methods: {
    isAdmin() {
      return this.userLevel(10);
    },

    // Alterna entre dispositivos do OnAgro e de terceiros. Os dois modos usam campos
    // diferentes, então trocar de origem descarta a seleção do modo anterior — manter
    // resíduo produziria um payload com device nativo E externo, que o banco rejeita.
    setOrigem(origem) {
      if (this.isLoading || this.isSubmitting) return;
      if (this.formData.origem === origem) return;

      this.formData.origem = origem;
      this.resetCondicoesDadoUnidade();

      if (origem === 'terceiros') {
        this.resetSensorSelection();
        this.formData.propriedadeId = '';
        this.formData.dispositivoId = '';
        this.selectedPropriedadeObj = null;
        this.selectedDispositivoObj = null;
        this.loadFontesExternas();
      } else {
        this.resetExternalSelection();
        this.selectedSensorFields = [];
      }
    },

    // Métodos para VueMultiselect
    onPropriedadeSelect(selected) {
      this.formData.propriedadeId = selected.id;
      this.onPropertyChange();
    },

    // --- Dispositivos de terceiros ---

    // Sem estação liberada, o seletor de origem some e o form segue no fluxo OnAgro.
    // Editar um alerta externo existente é exceção: aí o seletor precisa aparecer para
    // o usuário enxergar em que modo está.
    async carregarDisponibilidadeExterna() {
      try {
        const res = await this.urlGet("/ExternalDevice/Availability");
        this.temDispositivosExternos = !!(res.data && res.data.hasAccess);
      } catch (e) {
        // Admin sempre enxerga todas as estações: se a checagem falhar, mantém o seletor
        // visível em vez de escondê-lo por indisponibilidade momentânea.
        this.temDispositivosExternos = this.isAdmin();
      }

      if (this.formData && this.formData.externalDeviceId) {
        this.temDispositivosExternos = true;
        return;
      }

      // Perdeu o acesso com a origem já em 'terceiros' (ex.: admin revogou): volta ao padrão.
      if (!this.temDispositivosExternos && this.formData && this.formData.origem === "terceiros") {
        this.setOrigem("onagro");
      }
    },

    async loadFontesExternas() {
      if (this.fonteOptions.length) return;

      this.loadingFontes = true;
      try {
        const response = await this.urlGet('/ExternalDevice/Sources');
        this.fonteOptions = (response.data && response.data.itens) || [];
      } catch (error) {
        console.error('Erro ao carregar fontes de dispositivos externos:', error);
        this.fonteOptions = [];
        this.showAlert("Erro", "Erro ao carregar as fontes de dispositivos de terceiros", "bg-danger");
      } finally {
        this.loadingFontes = false;
      }
    },

    onFonteSelect(selected) {
      if (!selected) {
        this.resetExternalDeviceSelection();
        return;
      }

      this.formData.fonte = selected.fonte;
      // Trocar de fonte troca a lista de dispositivos: limpa a seleção e as condições.
      this.resetExternalDeviceSelection();
      this.loadExternalDevices(selected.fonte);
    },

    // Sem fonte, o endpoint devolve todas as estações ativas — usado no prefill quando a
    // querystring traz só o id do dispositivo.
    async loadExternalDevices(fonte) {
      this.loadingExternalDevices = true;
      try {
        const url = fonte
          ? '/ExternalDevice/BySource?fonte=' + encodeURIComponent(fonte)
          : '/ExternalDevice/BySource';
        const response = await this.urlGet(url);
        this.externalDeviceOptions = (response.data && response.data.itens) || [];
      } catch (error) {
        console.error('Erro ao carregar dispositivos da fonte:', error);
        this.externalDeviceOptions = [];
        this.showAlert("Erro", "Erro ao carregar os dispositivos desta fonte", "bg-danger");
      } finally {
        this.loadingExternalDevices = false;
      }
    },

    onExternalDeviceSelect(selected) {
      if (!selected) {
        this.resetExternalDeviceSelection();
        return;
      }

      this.formData.externalDeviceId = selected.id;
      this.formData.externalId = selected.externalId;

      // As grandezas vêm dos sensores que a fonte declara para ESTA estação.
      this.selectedSensorFields = this.buildExternalFields(selected);
      this.resetCondicoesDadoUnidade();
    },

    // Traduz o bloco "sensores" da estação ({"nivel":true,...}) para a mesma estrutura de
    // fields que o fluxo de sensor próprio usa — assim os selects de Dado/Unidade e toda a
    // lógica de condição funcionam sem saber que o dispositivo é externo.
    buildExternalFields(device) {
      const sensores = (device && device.sensores) || {};
      const fields = [];
      const vistos = new Set();

      Object.keys(sensores).forEach((sensor) => {
        if (!sensores[sensor]) return;
        const doSensor = this.externalFieldsBySensor[sensor];
        if (!doSensor) return;

        doSensor.forEach((f) => {
          // Estações com nível + estação repetem grandezas; a primeira ocorrência vence.
          if (vistos.has(f.dataToAlert)) return;
          vistos.add(f.dataToAlert);
          fields.push(f);
        });
      });

      return fields;
    },

    // Limpa só a estação escolhida (mantém a lista da fonte atual).
    resetExternalDeviceSelection() {
      this.selectedExternalDeviceObj = null;
      this.formData.externalDeviceId = null;
      this.formData.externalId = null;
      this.selectedSensorFields = [];
      this.resetCondicoesDadoUnidade();
    },

    resetExternalSelection() {
      this.selectedFonteObj = null;
      this.selectedExternalDeviceObj = null;
      this.externalDeviceOptions = [];
      this.formData.fonte = null;
      this.formData.externalDeviceId = null;
      this.formData.externalId = null;
    },

    onDispositivoSelect(selected) {
      this.formData.dispositivoId = selected.id;
      // Adia os resets/carregamento de sensores para o próximo tick, para não interferir
      // na atualização interna do v-model do VueMultiselect no mesmo evento de seleção.
      this.$nextTick(() => {
        // Ao trocar de dispositivo os sensores (e portanto os Dados/Unidades) mudam:
        // limpar o sensor selecionado e as condições, e recarregar.
        this.resetSensorSelection();
        this.resetCondicoesDadoUnidade();
        this.loadSensorsByDevice(selected.id);
      });
    },

    // Carrega os sensores do dispositivo (mesmo endpoint usado no Relatório).
    async loadSensorsByDevice(qrCodeId) {
      if (!qrCodeId) {
        this.sensorOptions = [];
        return;
      }

      this.loadingSensors = true;

      try {
        const response = await this.urlPost('/Station/GetSensorByQrCodeId/' + qrCodeId);
        const itens = (response.data && response.data.itens) || [];
        this.sensorOptions = itens.map(sensor => ({
          linkname: sensor.linkname,
          nome: sensor.nome,
          name: sensor.nome,
          sourceIds: sensor.sourceIds || [],
          fields: sensor.fields || []
        }));
      } catch (error) {
        console.error('Erro ao carregar sensores do dispositivo:', error);
        this.sensorOptions = [];
        this.showAlert("Erro", "Erro ao carregar sensores do dispositivo", "bg-danger");
      } finally {
        this.loadingSensors = false;
      }
    },

    onSensorSelect(sensor) {
      if (!sensor) {
        this.resetSensorSelection();
        this.resetCondicoesDadoUnidade();
        return;
      }

      this.formData.sensorName = sensor.linkname;
      const sources = sensor.sourceIds || [];
      this.formData.sourceId = (sources.length === 1 && sources[0] !== 'default')
        ? sources[0]
        : null;

      // Guarda os fields do sensor: fonte dos Dados e Unidades disponíveis.
      this.selectedSensorFields = sensor.fields || [];

      // Ao trocar de sensor, os Dados/Unidades mudam: limpar as condições.
      this.resetCondicoesDadoUnidade();
    },

    // Limpa a seleção de sensor (mantém a lista de opções).
    resetSensorSelection() {
      this.selectedSensorObj = null;
      this.selectedSensorFields = [];
      this.formData.sensorName = '';
      this.formData.sourceId = null;
    },

    // Limpa o Dado e a Unidade de todas as condições (mantém operador/valor).
    resetCondicoesDadoUnidade() {
      if (this.formData.condicoes && Array.isArray(this.formData.condicoes)) {
        this.formData.condicoes.forEach((condicao) => {
          condicao.dataToAlert = '';
          condicao.unit = '';
          condicao.selectedDataToAlertObj = null;
          condicao.selectedUnitObj = null;
        });
      }
    },

    onAlertFrequencySelect(selected) {
      this.formData.alertFrequency = selected.value;
    },

    onAlertTypeSelect(selected) {
      this.formData.alertType = selected.value;
    },

    onDataToAlertSelect(condicao, selected) {
      if (selected) {
        condicao.dataToAlert = selected.value;
        this.onDataTypeChange(condicao);
      } else {
        // Quando limpa a seleção
        condicao.dataToAlert = '';
        condicao.unit = '';
        condicao.selectedUnitObj = null;
      }
    },

    onOperatorSelect(condicao, selected) {
      if (selected) {
        condicao.operator = selected.value;
      } else {
        condicao.operator = '';
      }
    },

    onUnitSelect(condicao, selected) {
      if (selected) {
        condicao.unit = selected.value;
      } else {
        condicao.unit = '';
      }
    },

    mapearCampos() {
      if (!this.formData) return;

      // Alerta de dispositivo de terceiros (edição/prefill): liga o modo pela origem.
      if (this.formData.externalDeviceId) {
        this.formData.origem = 'terceiros';
      } else if (this.formData.isSharedDevice) {
        this.formData.propriedadeId = 'shared';
      } else if (this.formData.propriertyId !== undefined && !this.formData.propriedadeId) {
        this.formData.propriedadeId = Number(this.formData.propriertyId);
      }

      if (this.formData.alertFrequency !== undefined) {
        this.formData.alertFrequency = Number(this.formData.alertFrequency);
      }

      if (this.formData.alertType !== undefined) {
        this.formData.alertType = Number(this.formData.alertType);
      }

      // 'shared' ("Compartilhados Comigo") não é numérico: preservar a string.
      if (this.formData.propriedadeId !== undefined
          && this.formData.propriedadeId !== ''
          && this.formData.propriedadeId !== 'shared') {
        this.formData.propriedadeId = Number(this.formData.propriedadeId);
      }

      if (this.formData.dispositivoId !== undefined) {
        this.formData.dispositivoId = Number(this.formData.dispositivoId);
      }

      // Garantir que sensorName/sourceId existam no formData (reatividade).
      if (this.formData.sensorName === undefined) {
        this.formData.sensorName = '';
      }
      if (this.formData.sourceId === undefined) {
        this.formData.sourceId = null;
      }

      // Idem para os campos de dispositivo de terceiros. 'origem' controla qual metade do
      // formulário aparece; default OnAgro (comportamento de antes da integração).
      if (!this.formData.origem) {
        this.formData.origem = this.formData.externalDeviceId ? 'terceiros' : 'onagro';
      }
      if (this.formData.fonte === undefined) {
        this.formData.fonte = null;
      }
      if (this.formData.externalDeviceId === undefined) {
        this.formData.externalDeviceId = null;
      }
      if (this.formData.externalId === undefined) {
        this.formData.externalId = null;
      }

      if (!this.formData.condicoes || !Array.isArray(this.formData.condicoes)) {
        this.formData.condicoes = [{
          dataToAlert: '',
          operator: '',
          value: '',
          unit: '',
          selectedDataToAlertObj: null,
          selectedOperatorObj: null,
          selectedUnitObj: null
        }];
      } else {
        this.formData.condicoes.forEach((cond, index) => {
          if (cond.dataToAlert !== undefined && cond.dataToAlert !== '' && cond.dataToAlert !== null) {
            const value = Number(cond.dataToAlert);
            this.formData.condicoes[index].dataToAlert = value;
            // Criar objeto selecionado
            const selected = this.dataToAlertOptionsArray.find(opt => opt.value === value);
            this.formData.condicoes[index].selectedDataToAlertObj = selected || null;
          } else {
            this.formData.condicoes[index].selectedDataToAlertObj = null;
          }

          if (cond.operator !== undefined && cond.operator !== '' && cond.operator !== null) {
            const value = Number(cond.operator);
            this.formData.condicoes[index].operator = value;
            // Criar objeto selecionado
            const selected = this.operationTypeOptionsArray.find(opt => opt.value === value);
            this.formData.condicoes[index].selectedOperatorObj = selected || null;
          } else {
            this.formData.condicoes[index].selectedOperatorObj = null;
          }

          if (cond.value !== undefined && cond.value !== '' && cond.value !== null) {
            this.formData.condicoes[index].value = Number(cond.value);
          }

          if (cond.unit !== undefined && cond.unit !== '' && cond.unit !== null) {
            const value = Number(cond.unit);
            this.formData.condicoes[index].unit = value;
            // Criar objeto selecionado
            const units = this.getAvailableUnitsArray(cond);
            const selected = units.find(opt => opt.value === value);
            this.formData.condicoes[index].selectedUnitObj = selected || null;
          } else {
            this.formData.condicoes[index].selectedUnitObj = null;
          }
        });
      }

      // Sincronizar objetos selecionados com os valores do formData
      this.syncSelectedObjects();
    },

    syncSelectedObjects() {
      if (!this.formData) return;

      // Sincronizar propriedade
      if (this.formData.propriedadeId) {
        this.selectedPropriedadeObj = this.propriedadeOptions.find(
          prop => prop.id === this.formData.propriedadeId
        ) || null;
      } else {
        this.selectedPropriedadeObj = null;
      }

      // Sincronizar dispositivo
      if (this.formData.dispositivoId && this.dispositivos.length > 0) {
        this.selectedDispositivoObj = this.dispositivos.find(
          disp => disp.id === this.formData.dispositivoId
        ) || null;
      } else {
        this.selectedDispositivoObj = null;
      }

      // Sincronizar frequência de alerta
      if (this.formData.alertFrequency !== undefined && this.formData.alertFrequency !== '' && this.formData.alertFrequency !== null) {
        const frequency = Number(this.formData.alertFrequency);
        this.selectedAlertFrequencyObj = this.alertFrequencyOptionsArray.find(
          opt => opt.value === frequency
        ) || null;
      } else {
        this.selectedAlertFrequencyObj = null;
      }

      // Sincronizar tipo de alerta
      if (this.formData.alertType !== undefined && this.formData.alertType !== '' && this.formData.alertType !== null) {
        const type = Number(this.formData.alertType);
        this.selectedAlertTypeObj = this.alertTypeOptionsArray.find(
          opt => opt.value === type
        ) || null;
      } else {
        this.selectedAlertTypeObj = null;
      }

      // Sincronizar condições
      if (this.formData.condicoes && Array.isArray(this.formData.condicoes)) {
        this.formData.condicoes.forEach((condicao) => {
          // Sincronizar dataToAlert
          if (condicao.dataToAlert !== undefined && condicao.dataToAlert !== '' && condicao.dataToAlert !== null) {
            const value = Number(condicao.dataToAlert);
            const selected = this.dataToAlertOptionsArray.find(opt => opt.value === value);
            condicao.selectedDataToAlertObj = selected || null;
          }

          // Sincronizar operator
          if (condicao.operator !== undefined && condicao.operator !== '' && condicao.operator !== null) {
            const value = Number(condicao.operator);
            const selected = this.operationTypeOptionsArray.find(opt => opt.value === value);
            condicao.selectedOperatorObj = selected || null;
          }

          // Sincronizar unit
          if (condicao.unit !== undefined && condicao.unit !== '' && condicao.unit !== null) {
            const value = Number(condicao.unit);
            const units = this.getAvailableUnitsArray(condicao);
            const selected = units.find(opt => opt.value === value);
            condicao.selectedUnitObj = selected || null;
          }
        });
      }

      // Alerta de terceiros: pré-seleciona fonte e estação; senão, o sensor salvo.
      if (this.isExternalMode) {
        this.prefillExternalForEdit();
      } else {
        this.prefillSensorForEdit();
      }

      // Forçar atualização da interface
    },

    // Edição/prefill de alerta de terceiros: carrega as fontes, seleciona a do alerta,
    // carrega as estações dessa fonte e seleciona a salva — restaurando os Dados/Unidades.
    async prefillExternalForEdit() {
      await this.loadFontesExternas();

      if (this.formData.fonte) {
        this.selectedFonteObj = this.fonteOptions.find(f => f.fonte === this.formData.fonte) || null;
      }

      if (!this.formData.externalDeviceId) return;

      // Sem fonte salva (ex.: prefill só com o id), busca em todas as estações ativas.
      if (!this.externalDeviceOptions.length) {
        await this.loadExternalDevices(this.formData.fonte || '');
      }

      const device = this.externalDeviceOptions.find(
        d => Number(d.id) === Number(this.formData.externalDeviceId)
      );
      if (!device) return;

      this.selectedExternalDeviceObj = device;
      this.formData.externalId = device.externalId;
      this.selectedSensorFields = this.buildExternalFields(device);

      // Prefill sem 'fonte' na URL: agora que a estação é conhecida, resolve a fonte dela
      // para o select não ficar vazio.
      if (!this.formData.fonte && device.fonte) {
        this.formData.fonte = device.fonte;
        this.selectedFonteObj = this.fonteOptions.find(f => f.fonte === device.fonte) || null;
      }

      // Agora que os fields existem, ressincroniza Dado/Unidade das condições.
      if (this.formData.condicoes && Array.isArray(this.formData.condicoes)) {
        this.formData.condicoes.forEach((condicao) => {
          if (condicao.dataToAlert !== undefined && condicao.dataToAlert !== '' && condicao.dataToAlert !== null) {
            const value = Number(condicao.dataToAlert);
            condicao.selectedDataToAlertObj = this.dataToAlertOptionsArray.find(opt => opt.value === value) || null;
          }
          if (condicao.unit !== undefined && condicao.unit !== '' && condicao.unit !== null) {
            const value = Number(condicao.unit);
            const units = this.getAvailableUnitsArray(condicao);
            condicao.selectedUnitObj = units.find(opt => opt.value === value) || null;
          }
        });
      }
    },

    // Ao editar um alerta existente, carrega os sensores do dispositivo,
    // pré-seleciona o sensor salvo e ressincroniza Dados/Unidades das condições.
    // Degrada bem: se o alerta não tiver sensorName (alertas antigos), apenas
    // deixa o sensor sem seleção — o usuário precisará escolher para editar.
    async prefillSensorForEdit() {
      if (!this.formData || !this.formData.dispositivoId) return;

      // SEMPRE carrega os sensores do dispositivo ao editar (mesmo que o alerta
      // não tenha sensor salvo — alertas antigos). Assim o dropdown já vem populado.
      if (!this.sensorOptions.length) {
        await this.loadSensorsByDevice(this.formData.dispositivoId);
      }

      // Sem sensor salvo (alerta legado): dropdown fica populado, mas sem seleção.
      // O usuário escolhe o sensor para então liberar os Dados/Unidades.
      if (!this.formData.sensorName) return;

      // Evita reprocessar se já está selecionado corretamente.
      if (this.selectedSensorObj && this.selectedSensorObj.linkname === this.formData.sensorName) {
        return;
      }

      const sensor = this.sensorOptions.find(s => s.linkname === this.formData.sensorName);
      if (!sensor) {
        // Sensor salvo não está mais disponível no dispositivo: deixa sem seleção.
        return;
      }

      this.selectedSensorObj = sensor;
      this.selectedSensorFields = sensor.fields || [];

      // Ressincronizar os objetos de Dado/Unidade das condições agora que os
      // fields (opções) estão disponíveis.
      if (this.formData.condicoes && Array.isArray(this.formData.condicoes)) {
        this.formData.condicoes.forEach((condicao) => {
          if (condicao.dataToAlert !== undefined && condicao.dataToAlert !== '' && condicao.dataToAlert !== null) {
            const value = Number(condicao.dataToAlert);
            const selected = this.dataToAlertOptionsArray.find(opt => opt.value === value);
            condicao.selectedDataToAlertObj = selected || null;
          }
          if (condicao.unit !== undefined && condicao.unit !== '' && condicao.unit !== null) {
            const value = Number(condicao.unit);
            const units = this.getAvailableUnitsArray(condicao);
            const selected = units.find(opt => opt.value === value);
            condicao.selectedUnitObj = selected || null;
          }
        });
      }

    },
    
    inicializarFormulario() {
      if (!this.formData.condicoes || !this.formData.condicoes.length) {
        this.formData.condicoes = [
          {
            dataToAlert: '',
            operator: '',
            value: '',
            unit: '',
            selectedDataToAlertObj: null,
            selectedOperatorObj: null,
            selectedUnitObj: null
          }
        ];
      } else {
        this.formData.condicoes.forEach((condicao, index) => {
          if (condicao.dataToAlert !== undefined && condicao.dataToAlert !== '' && condicao.dataToAlert !== null) {
            const value = Number(condicao.dataToAlert);
            this.formData.condicoes[index].dataToAlert = value;
            const selected = this.dataToAlertOptionsArray.find(opt => opt.value === value);
            this.formData.condicoes[index].selectedDataToAlertObj = selected || null;
          } else {
            this.formData.condicoes[index].selectedDataToAlertObj = null;
          }

          if (condicao.operator !== undefined && condicao.operator !== '' && condicao.operator !== null) {
            const value = Number(condicao.operator);
            this.formData.condicoes[index].operator = value;
            const selected = this.operationTypeOptionsArray.find(opt => opt.value === value);
            this.formData.condicoes[index].selectedOperatorObj = selected || null;
          } else {
            this.formData.condicoes[index].selectedOperatorObj = null;
          }

          if (condicao.value !== undefined && condicao.value !== '' && condicao.value !== null) {
            this.formData.condicoes[index].value = Number(condicao.value);
          }

          if (condicao.unit !== undefined && condicao.unit !== '' && condicao.unit !== null) {
            const value = Number(condicao.unit);
            this.formData.condicoes[index].unit = value;
            const units = this.getAvailableUnitsArray(condicao);
            const selected = units.find(opt => opt.value === value);
            this.formData.condicoes[index].selectedUnitObj = selected || null;
          } else {
            this.formData.condicoes[index].selectedUnitObj = null;
          }

          if (condicao.dataToAlert !== '' && condicao.dataToAlert !== undefined &&
              (condicao.unit === '' || condicao.unit === undefined || condicao.unit === null)) {
            this.onDataTypeChange(condicao);
          }
        });
      }

    },
    
    // Rótulo de uma unidade (índice do enum MeasurementUnit). Reaproveita o
    // mapa fixo unitsByDataType apenas para exibir 'lux', '°C', '%', etc.
    getUnitLabel(unitIndex) {
      const idx = Number(unitIndex);
      for (const dataType in this.unitsByDataType) {
        const units = this.unitsByDataType[dataType];
        if (Object.prototype.hasOwnProperty.call(units, idx)) {
          return units[idx];
        }
      }
      return String(unitIndex);
    },

    // Encontra o field (backend) do sensor correspondente ao Dado da condição.
    getFieldForCondicao(condicao) {
      const hasValidDataToAlert = condicao.dataToAlert !== '' &&
                                   condicao.dataToAlert !== undefined &&
                                   condicao.dataToAlert !== null;

      if (!hasValidDataToAlert && condicao.dataToAlert !== 0) {
        return null;
      }

      const dataType = Number(condicao.dataToAlert);
      return (this.selectedSensorFields || []).find(
        f => Number(f.dataToAlert) === dataType
      ) || null;
    },

    getAvailableUnitsArray(condicao) {
      // As unidades disponíveis vêm do field do sensor (availableUnits).
      const field = this.getFieldForCondicao(condicao);
      if (!field || !Array.isArray(field.availableUnits)) {
        return [];
      }
      return field.availableUnits.map(u => ({
        value: Number(u),
        label: this.getUnitLabel(u)
      }));
    },

    onDataTypeChange(condicao) {
      const dataType = Number(condicao.dataToAlert);

      // Limpar unidade atual ao trocar de dado
      condicao.unit = '';
      condicao.selectedUnitObj = null;

      if (dataType !== '' && !isNaN(dataType)) {
        // Unidade padrão vem do field do sensor (defaultUnit); se não houver
        // sensor/field, recorre ao mapa fixo defaultUnits como fallback.
        const field = this.getFieldForCondicao(condicao);
        const defaultUnit = (field && field.defaultUnit !== undefined && field.defaultUnit !== null)
          ? Number(field.defaultUnit)
          : this.defaultUnits[dataType];
        condicao.unit = defaultUnit;

        // Criar objeto selecionado para a unidade padrão
        const units = this.getAvailableUnitsArray(condicao);
        const selected = units.find(opt => opt.value === defaultUnit);
        condicao.selectedUnitObj = selected || null;

      }
    },
    
    async onPropertyChange() {
      // Limpar dispositivo quando trocar de propriedade
      this.formData.dispositivoId = "";
      this.selectedDispositivoObj = null;

      if (this.formData.propriedadeId === 'shared') {
        await this.loadSharedDispositivos();
      } else if (this.formData.propriedadeId) {
        await this.loadDispositivosByPropriedade(this.formData.propriedadeId);
      }
    },
    
    async loadDispositivosByPropriedade(propriedadeId) {
      if (!propriedadeId) {
        return;
      }
      
      this.loadingDispositivos = true;
      
      try {
        if (propriedadeId === 'shared') {
          this.$emit('load-shared-dispositivos');
        } else {
          this.$emit('load-dispositivos', propriedadeId);
        }
      } catch (error) {
        console.error('Erro ao solicitar carregamento de dispositivos:', error);
        this.showAlert("Erro", "Erro ao carregar dispositivos", "bg-danger");
      } finally {
        setTimeout(() => {
          this.loadingDispositivos = false;
        }, 500);
      }
    },
    
    async loadSharedDispositivos() {
      this.loadingDispositivos = true;
      
      try {
        this.$emit('load-shared-dispositivos');
      } catch (error) {
        console.error('Erro ao solicitar carregamento de dispositivos compartilhados:', error);
        this.showAlert("Erro", "Erro ao carregar dispositivos compartilhados", "bg-danger");
      } finally {
        setTimeout(() => {
          this.loadingDispositivos = false;
        }, 500);
      }
    },
    
    adicionarCondicao() {
      if (this.isSubmitting) return;

      const novaCondicao = {
        dataToAlert: '',
        operator: '',
        value: '',
        unit: '',
        selectedDataToAlertObj: null,
        selectedOperatorObj: null,
        selectedUnitObj: null
      };

      this.formData.condicoes.push(novaCondicao);
    },
    
    removerCondicao(index) {
      if (this.isSubmitting) return;
      
      this.formData.condicoes.splice(index, 1);
    },
    
    resetForm() {
      // Resetar todos os objetos selecionados
      this.selectedPropriedadeObj = null;
      this.selectedDispositivoObj = null;
      this.selectedAlertFrequencyObj = null;
      this.selectedAlertTypeObj = null;
      this.selectedSensorObj = null;
      this.selectedSensorFields = [];
      this.sensorOptions = [];
      this.selectedFonteObj = null;
      this.selectedExternalDeviceObj = null;
      this.externalDeviceOptions = [];

      // Resetar formData será feito pelo componente pai
      // Forçar atualização do componente
    },

    validateForm() {
      if (this.isSubmitting) {
        return false;
      }

      if (!this.formData.nome) {
        this.showAlert("Atenção", "Nome do alerta é obrigatório", "bg-warning");
        return false;
      }

      if (this.isExternalMode) {
        if (!this.formData.fonte) {
          this.showAlert("Atenção", "Selecione o órgão", "bg-warning");
          return false;
        }

        if (!this.formData.externalDeviceId) {
          this.showAlert("Atenção", "Selecione um dispositivo", "bg-warning");
          return false;
        }
      } else {
        // Propriedade só existe no fluxo OnAgro; estações de terceiros não pertencem a uma.
        if (!this.formData.propriedadeId) {
          this.showAlert("Atenção", "Selecione uma propriedade", "bg-warning");
          return false;
        }

        if (!this.formData.dispositivoId) {
          this.showAlert("Atenção", "Selecione um dispositivo", "bg-warning");
          return false;
        }

        if (!this.formData.sensorName) {
          this.showAlert("Atenção", "Selecione um sensor", "bg-warning");
          return false;
        }
      }

      if (this.formData.alertType === undefined || this.formData.alertType === '') {
        this.showAlert("Atenção", "Selecione o tipo de alerta", "bg-warning");
        return false;
      }

      if (this.formData.alertFrequency === undefined || this.formData.alertFrequency === '') {
        this.showAlert("Atenção", "Selecione a frequência de alerta", "bg-warning");
        return false;
      }

      if (!this.formData.condicoes || !Array.isArray(this.formData.condicoes) || this.formData.condicoes.length === 0) {
        this.showAlert("Atenção", "Adicione pelo menos uma condição", "bg-warning");
        return false;
      }

      for (let i = 0; i < this.formData.condicoes.length; i++) {
        const condicao = this.formData.condicoes[i];
        if (
          condicao.dataToAlert === undefined || condicao.dataToAlert === '' ||
          condicao.operator === undefined || condicao.operator === '' ||
          condicao.value === undefined || condicao.value === '' ||
          condicao.unit === undefined || condicao.unit === ''
        ) {
          this.showAlert("Atenção", `Preencha todos os campos da condição ${i + 1}`, "bg-warning");
          return false;
        }

        // Negativos são válidos (ex.: temperatura <= -10 °C).
        if (isNaN(Number(condicao.value))) {
          this.showAlert("Atenção", `O valor da condição ${i + 1} deve ser um número válido`, "bg-warning");
          return false;
        }
      }

      return true;
    },

    showAlert(title, message, type, duration = 3000) {
      let alertComponent = this.$parent.$refs.alert;
      
      if (!alertComponent && this.$root.$refs.alert) {
        alertComponent = this.$root.$refs.alert;
      }
      
      if (!alertComponent) {
        let current = this.$parent;
        while (current && !alertComponent) {
          if (current.$refs && current.$refs.alert) {
            alertComponent = current.$refs.alert;
            break;
          }
          current = current.$parent;
        }
      }

      if (alertComponent && alertComponent.show) {
        alertComponent.show(title, message, null, duration, type);
      } else {
        console.warn(`${title}: ${message}`);
      }
    }
  },
  watch: {
    'formData.propriedadeId': {
      handler(newValue, oldValue) {
        // Sincronizar propriedade selecionada quando o valor mudar
        if (newValue !== oldValue && newValue) {
          this.selectedPropriedadeObj = this.propriedadeOptions.find(
            prop => prop.id === newValue
          ) || null;
        }
      },
      immediate: false
    },
    'formData.dispositivoId': {
      handler(newValue, oldValue) {
        // Sincronizar dispositivo selecionado quando o valor mudar
        if (newValue !== oldValue && newValue && this.dispositivos.length > 0) {
          this.selectedDispositivoObj = this.dispositivos.find(
            disp => disp.id === newValue
          ) || null;
        }
      },
      immediate: false
    },
    'formData.alertFrequency': {
      handler(newValue, oldValue) {
        // Sincronizar frequência selecionada quando o valor mudar
        if (newValue !== oldValue && newValue !== '' && newValue !== undefined) {
          this.selectedAlertFrequencyObj = this.alertFrequencyOptionsArray.find(
            opt => opt.value === newValue
          ) || null;
        }
      },
      immediate: false
    },
    'formData.alertType': {
      handler(newValue, oldValue) {
        // Sincronizar tipo selecionado quando o valor mudar
        if (newValue !== oldValue && newValue !== '' && newValue !== undefined) {
          this.selectedAlertTypeObj = this.alertTypeOptionsArray.find(
            opt => opt.value === newValue
          ) || null;
        }
      },
      immediate: false
    },
    dispositivos: {
      handler(newDispositivos) {
        this.loadingDispositivos = false;
        // Sincronizar dispositivo selecionado quando a lista mudar
        if (this.formData.dispositivoId && newDispositivos.length > 0) {
          this.selectedDispositivoObj = newDispositivos.find(
            disp => disp.id === this.formData.dispositivoId
          ) || null;
          // Na edição, os dispositivos podem chegar depois do form abrir: agora que
          // o dispositivo está resolvido, carrega os sensores e pré-seleciona o salvo.
          this.prefillSensorForEdit();
        }
      },
      immediate: true
    },
    propriedades: {
      handler(newPropriedades) {
        // Sincronizar propriedade selecionada quando a lista mudar
        if (this.formData && this.formData.propriedadeId && newPropriedades.length > 0) {
          // Aguardar um tick para garantir que propriedadeOptions esteja atualizado
          this.$nextTick(() => {
            this.selectedPropriedadeObj = this.propriedadeOptions.find(
              prop => prop.id === this.formData.propriedadeId
            ) || null;
          });
        }
      },
      immediate: true,
      deep: false
    },
    isSubmitting: {
      handler(newValue) {
        if (newValue) {
          this.isLoading = false;
        }
      },
      immediate: true
    },
    'formData.condicoes': {
      handler(newCondicoes) {
        // Não executar durante submissão ou loading
        if (this.isLoading || this.isSubmitting) {
          return;
        }

        if (newCondicoes && Array.isArray(newCondicoes)) {
          newCondicoes.forEach((condicao) => {
            // Verificar se selectedDataToAlertObj mudou
            if (condicao.selectedDataToAlertObj && condicao.selectedDataToAlertObj.value !== undefined) {
              const newValue = condicao.selectedDataToAlertObj.value;
              if (condicao.dataToAlert !== newValue) {
                condicao.dataToAlert = newValue;
                this.onDataTypeChange(condicao);
              }
            }

            // Verificar se selectedOperatorObj mudou
            if (condicao.selectedOperatorObj && condicao.selectedOperatorObj.value !== undefined) {
              const newValue = condicao.selectedOperatorObj.value;
              if (condicao.operator !== newValue) {
                condicao.operator = newValue;
              }
            }

            // Verificar se selectedUnitObj mudou
            if (condicao.selectedUnitObj && condicao.selectedUnitObj.value !== undefined) {
              const newValue = condicao.selectedUnitObj.value;
              if (condicao.unit !== newValue) {
                condicao.unit = newValue;
              }
            }
          });
        }
      },
      deep: true,
      immediate: false
    }
  }
};
</script>

<style scoped>
/* Seletor de origem (OnAgro / Terceiros) — pílula segmentada, mesma linguagem do
   switch de modos do mapa. */

/* O tema deixa .form-label inline, então sem isto o toggle (inline-flex) sobe para a
   mesma linha do rótulo — diferente de todos os outros campos do formulário. */
.origem-label {
  display: block;
  margin-bottom: 0.5rem;
}

.origem-toggle {
  display: inline-flex;
  background: #f1f3f5;
  border: 1px solid #ced4da;
  border-radius: 26px;
  padding: 3px;
  gap: 2px;
}

.origem-option {
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 14px;
  font-weight: 600;
  padding: 7px 22px;
  border-radius: 22px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.origem-option:hover:not(.active):not(:disabled) {
  color: #006035;
}

.origem-option.active {
  background: #006035;
  color: #ffffff;
}

.origem-option:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

@media (max-width: 576px) {
  .origem-toggle {
    display: flex;
    width: 100%;
  }
  .origem-option {
    flex: 1 1 0;
    padding: 7px 10px;
  }
}

.dados-container {
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: #f8f9fa;
}

.dados-item {
  position: relative;
  padding-bottom: 1rem;
  border-bottom: 1px dashed #ced4da;
}

.dados-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 0.25rem;
}

.submitting-overlay {
  background-color: rgba(248, 249, 250, 0.95);
  backdrop-filter: blur(3px);
  z-index: 1050;
}

.submitting-overlay .loading-content {
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e9ecef;
}

.loading-content {
  text-align: center;
}

.loading-content .spinner-border {
  width: 3rem;
  height: 3rem;
}

.form-loading {
  opacity: 0.3;
  pointer-events: none;
}

.container-fluid {
  transition: opacity 0.3s ease-in-out;
}

.text-danger {
  color: #dc3545 !important;
}

.form-select:disabled {
  background-color: #e9ecef;
  opacity: 0.65;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

@media (max-width: 768px) {
  .col-md-6 {
    margin-bottom: 1rem;
  }

  .submitting-overlay .loading-content {
    margin: 1rem;
    padding: 1.5rem;
  }
}

/* O placeholder/valor do VueMultiselect não quebra linha por padrão: em coluna estreita
   o texto vazava para fora da caixa em vez de truncar. */
.dados-item :deep(.multiselect__single),
.dados-item :deep(.multiselect__placeholder) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* As colunas seguem a largura do MODAL, não a da tela: o modal do Crud tem ~500px mesmo
   num tablet, então os breakpoints do Bootstrap (col-sm em 576px) quase nunca disparam
   aqui — era isso que mantinha as 4 colunas espremidas.

   Container queries resolveriam de forma mais direta, mas o browserslist do projeto
   ainda alcança Safari 15, que não as suporta. Grid com minmax() dá o mesmo efeito
   (quebra pela largura real disponível) com suporte universal. */
.dados-item .row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-left: 0;
  margin-right: 0;
}

.dados-item .row > [class*="col-"] {
  width: auto;
  max-width: none;
  flex: none;
  padding-left: 0;
  padding-right: 0;
  margin-bottom: 0 !important;
}

.form-select:disabled {
  cursor: not-allowed;
}

.progress {
  height: 8px;
  background-color: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  border-radius: 10px;
}

.submitting-overlay {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.submitting-overlay .loading-content {
  animation: slideIn 0.4s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Estilos para VueMultiselect */
.multiselect {
  min-height: 42px;
}

.multiselect__tags {
  min-height: 42px;
  padding: 8px 40px 0 8px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.multiselect__placeholder {
  color: #6c757d;
  padding-top: 0;
  margin-bottom: 8px;
}

.multiselect__single {
  margin-bottom: 8px;
  padding-top: 0;
  color: #495057;
}

.multiselect__content-wrapper {
  max-height: 300px !important;
  border: 1px solid #ced4da;
  border-top: none;
}

/* Opção ao passar o mouse (hover) */
.multiselect__option--highlight,
.multiselect .multiselect__option--highlight {
  background: #006035 !important;
  color: white !important;
  outline: none !important;
}

/* Opção selecionada (mas sem hover) */
.multiselect__option--selected,
.multiselect .multiselect__option--selected {
  background: #d4edda !important;
  color: #155724 !important;
  font-weight: 600 !important;
}

/* Opção ao passar o mouse e já estar selecionada */
.multiselect__option--selected.multiselect__option--highlight,
.multiselect .multiselect__option--selected.multiselect__option--highlight {
  background: #004d29 !important;
  color: white !important;
  font-weight: 600 !important;
}

/* Remove a cor rosa padrão */
.multiselect__option--selected:after {
  content: none !important;
}

/* Tag selecionada (modo múltiplo) */
.multiselect__tag {
  background: #006035 !important;
  color: white !important;
}

.multiselect__tag-icon:hover {
  background: #004d29 !important;
}

/* Focus state */
.multiselect--active .multiselect__tags {
  border-color: #006035;
  box-shadow: 0 0 0 0.2rem rgba(0, 96, 53, 0.25);
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .multiselect__content-wrapper {
    max-height: 250px !important;
  }

  .multiselect__option {
    padding: 12px;
    min-height: 44px;
    font-size: 16px;
  }
}

</style>

<style>
/* Estilos globais para VueMultiselect (sem scoped para sobrescrever os padrões da biblioteca) */

/* Opção ao passar o mouse (hover) */
.multiselect__option--highlight {
  background: #006035 !important;
  color: white !important;
  outline: none !important;
}

/* Opção selecionada (mas sem hover) */
.multiselect__option--selected {
  background: #d4edda !important;
  color: #155724 !important;
  font-weight: 600 !important;
}

/* Opção ao passar o mouse e já estar selecionada */
.multiselect__option--selected.multiselect__option--highlight {
  background: #004d29 !important;
  color: white !important;
  font-weight: 600 !important;
}

/* Remove o checkmark rosa padrão */
.multiselect__option--selected:after {
  content: none !important;
}

/* Tag selecionada */
.multiselect__tag {
  background: #006035 !important;
  color: white !important;
}

.multiselect__tag-icon:hover {
  background: #004d29 !important;
}

/* Focus state */
.multiselect--active .multiselect__tags {
  border-color: #006035 !important;
}
</style>
